import styled from "styled-components";
import WhiteTickIcon from "@assets/icons/tick-white.svg";
import mediaQuery from "@utils/mediaQuery";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";
import urlFor from "@utils/sanityImageBuilder";

export interface CategoryProps {
  image?: SanityImageSource;
  flow: string;
  name: string;
}

interface Props extends CategoryProps {
  selectedValue: string;
  onClick: (name: string, flow: string) => void;
  showIcon?: boolean; // NEW: Control whether icons should be displayed
}

// OPTIMIZED: Smaller tiles on mobile for better space usage
const Container = styled.button<{ hasIcon?: boolean }>`
  position: relative;
  display: flex;
  width: 140px; /* Reduced from 163px */
  min-height: 120px; /* Reduced from 138px */
  flex-direction: column;
  align-items: center;
  justify-content: ${props => props.hasIcon ? 'flex-start' : 'center'};
  border-radius: 8px;
  border: 1px solid #c3d4e1;
  background-color: var(--bg-card-alt);
  padding: 14px 12px; /* Reduced from 18px 16px */
  cursor: pointer;

  &:hover {
    border: 1px solid #018786;
  }

  ${mediaQuery("tablet")`
    width: 187px;
    min-height: 138px;
    padding: 18px 16px;
  `}
`;

const Name = styled.span`
  color: #000;
  text-align: center;
  font-size: var(--font-size-base); /* Slightly reduced from 16px on mobile */
  font-weight: var(--font-weight-normal);
  line-height: 150%; /* Tighter line height */

  ${mediaQuery("tablet")`
    font-size: var(--font-size-xl);
    line-height: 160%;
  `}
`;

const IconContainer = styled.span`
  width: 48px; /* Reduced from 58px */
  height: 48px;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 4px;
  background: var(--bg-card);

  img {
    width: 40px; /* Reduced from 48px */
    height: 40px;
    object-fit: cover;
  }
  
  ${mediaQuery("tablet")`
    width: 58px;
    height: 58px;
    
    img {
      width: 48px;
      height: 48px;
    }
  `}
`;

const Tick = styled.span<{ isActive?: boolean }>`
  visibility: ${(props) => (props.isActive ? "visual" : "hidden")};
  position: absolute;
  top: 8px; /* Adjusted for smaller tile */
  right: 8px;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
  border-style: solid;
  border-width: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-color: var(--interactive-primary);
  background-color: ${(props) => (props.isActive ? "var(--interactive-primary)" : "inherit")};
`;

const StyledWhiteTickIcon = styled(WhiteTickIcon)`
  width: 0.75rem;
  height: 0.75rem;
`;

const Category = ({ 
  image, 
  name, 
  flow, 
  selectedValue, 
  onClick,
  showIcon = true // NEW: Default to true for backward compatibility
}: Props) => {
  // Determine if icon should be rendered
  const shouldShowIcon = showIcon && image;

  return (
    <Container hasIcon={shouldShowIcon} onClick={() => onClick(name, flow)}>
      <Tick isActive={selectedValue === name}>
        <StyledWhiteTickIcon />
      </Tick>
      {shouldShowIcon && (
        <IconContainer>
          <img
            src={urlFor(image).width(48).height(48).url()}
            alt={`${name} icon`}
          />
        </IconContainer>
      )}
      <Name>{name}</Name>
    </Container>
  );
};

export default Category;